'use client'

import { useState, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import posthog from 'posthog-js'
import { canShowCapped, recordCappedShow } from '@/lib/popupSession'

interface ResourceConfig {
  title: string
  description: string
  bulletPoints: string[]
  ctaText: string
}

interface ScrollResourcePopupProps {
  postId: string
  resource: ResourceConfig
  capKey?: string
}

const DISMISSED_KEY = 'aibc_resource_dismissed'

export default function ScrollResourcePopup({ postId, resource, capKey }: ScrollResourcePopupProps) {
  const [visible, setVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const dismiss = useCallback(() => {
    setVisible(false)
    try {
      const dismissed = JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '{}')
      dismissed[postId] = Date.now()
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed))
    } catch {}
  }, [postId])

  useEffect(() => {
    try {
      const dismissed = JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '{}')
      if (dismissed[postId]) return
    } catch {}

    if (!canShowCapped(capKey)) return

    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)
      if (scrollPercent >= 0.33) {
        if (!canShowCapped(capKey)) {
          window.removeEventListener('scroll', handleScroll)
          return
        }
        recordCappedShow(capKey)
        setVisible(true)
        window.removeEventListener('scroll', handleScroll)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [postId, capKey])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setStatus('loading')
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: `resource_${postId}` }),
      })
      if (res.ok) {
        setStatus('success')

        if (posthog?.__loaded) {
          posthog.capture('blog_resource_signup', { post_id: postId })
        }
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', {
            content_name: `resource_download_${postId}`,
            content_category: 'free_resource',
          })
        }
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            send_to: 'AW-17984603839/20ewCKLx7pkcEL-N3f9C',
            value: 1.0,
            currency: 'AUD',
          })
        }
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  if (!visible) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative w-full max-w-md bg-[#111] border border-gray-700 rounded-2xl p-6 sm:p-8 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {status === 'success' ? (
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 mb-4">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Check your inbox!</h3>
            <p className="text-gray-400 text-sm">We&apos;ve sent the resource to your email. Welcome to the AI Builder community.</p>
            <button onClick={dismiss} className="mt-4 text-sm text-gray-500 hover:text-white transition-colors">
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="mb-1">
              <span className="inline-block text-xs font-semibold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full mb-3">
                FREE RESOURCE
              </span>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{resource.title}</h3>
            <p className="text-gray-400 text-sm mb-4">{resource.description}</p>
            <ul className="space-y-2 mb-5">
              {resource.bulletPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <span className="text-blue-400 mt-0.5 shrink-0">&#10003;</span>
                  {point}
                </li>
              ))}
            </ul>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                className="w-full bg-white/5 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors text-sm"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-white text-black font-semibold py-3 rounded-lg text-sm hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                {status === 'loading' ? 'Sending...' : resource.ctaText}
              </button>
              {status === 'error' && (
                <p className="text-red-400 text-xs text-center">Something went wrong. Try again.</p>
              )}
            </form>
            <p className="text-xs text-gray-600 text-center mt-3">
              Free newsletter included. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
